'use client';

import { useEffect, useRef, useState } from 'react';

import type { RowSelectionState, Table, TableOptions } from '@tanstack/react-table';

/**
 * Enables native row selection on a table. When provided, a leading checkbox
 * column is prepended and selection state is tracked, keyed by the table's
 * `getRowId` rather than by row index.
 */
export interface RowSelectionConfig<TData> {
  /**
   * Returns a reason a given row can't be selected — shown as a tooltip on the
   * (disabled) checkbox. Return `undefined` for selectable rows. Omit entirely
   * to make every row selectable.
   */
  rowSelectionDisabledReason?: (row: TData) => string | undefined;
}

type SelectionTableOptions<TData> = Pick<TableOptions<TData>, 'enableRowSelection' | 'onRowSelectionChange'>;

export interface UseRowSelectionResult<TData> {
  /** Whether selection is enabled. */
  enabled: boolean;
  /** Current selection state (controlled). */
  rowSelectionState: RowSelectionState;
  /** Clears the current selection. */
  clearSelection: () => void;
  /**
   * TanStack options to spread into `useReactTable` — everything except `state`,
   * which the caller merges with any other controlled state (e.g. sorting).
   * Empty when selection is disabled.
   */
  tableOptions: SelectionTableOptions<TData>;
  /** Selected row originals from a selection-configured table. */
  getSelectedRows: (table: Table<TData>) => TData[];
}

/**
 * Consolidates the row-selection state wiring shared by every selectable table:
 * controlled selection state, the `useReactTable` option spread
 * (`enableRowSelection`, `onRowSelectionChange`), selection derivation, and the
 * reset-on-change effect. Callers prepend the checkbox column themselves via
 * `createSelectionColumn` (the column must live at module scope for the
 * suspense wrapper's dummy header row), passing the same
 * `rowSelectionDisabledReason` so "has a reason" and "is disabled" stay in sync.
 *
 * `resetKey` should change whenever the visible row set is replaced (e.g. a
 * page/filter change) so a stale selection can't survive off-screen. Pass a
 * stable value to keep selection across renders (e.g. accumulated pages in
 * infinite scroll).
 */
export function useRowSelection<TData>(
  config: RowSelectionConfig<TData> | undefined,
  resetKey: unknown,
): UseRowSelectionResult<TData> {
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>({});

  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (lastResetKey.current === resetKey) return;
    lastResetKey.current = resetKey;
    if (config) setRowSelectionState({});
  }, [resetKey]);

  const clearSelection = () => setRowSelectionState({});

  const disabledReason = config?.rowSelectionDisabledReason;
  const tableOptions: SelectionTableOptions<TData> = config
    ? {
        // A row is selectable exactly when it has no disabled reason.
        enableRowSelection: disabledReason ? (row) => disabledReason(row.original) === undefined : true,
        onRowSelectionChange: setRowSelectionState,
      }
    : {};

  // Filtered, not core: under `clientData` a search narrows the list, and a bulk action
  // must not receive rows the user can no longer see. Identical to the core model when no
  // client filter is installed. Hiding a selected row only omits it while hidden; the
  // selection state still holds its id, so clearing the search brings it back.
  const getSelectedRows = (table: Table<TData>) =>
    config ? table.getFilteredSelectedRowModel().rows.map((r) => r.original) : [];

  return {
    enabled: !!config,
    rowSelectionState,
    clearSelection,
    tableOptions,
    getSelectedRows,
  };
}
